// src/pages/PartnerSupport.jsx
import React, { useState } from 'react';
import { 
  Search, 
  HelpCircle, 
  MessageSquare, 
  Phone, 
  Mail, 
  FileText, 
  Settings, 
  DollarSign, 
  Users, 
  Calendar, 
  ChevronRight, 
  ArrowRight, 
  Clock,
  CheckCircle 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/partner-support.css';
const PartnerSupport = () => {
  const [activeTab, setActiveTab] = useState('help-center');
  const [searchQuery, setSearchQuery] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    restaurantId: '',
    subject: '',
    category: '',
    message: '',
    priority: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const categories = [
    { id: 'account', name: 'Account Management', icon: <Users size={22} /> },
    { id: 'reservations', name: 'Reservations', icon: <Calendar size={22} /> },
    { id: 'payments', name: 'Payments & Billing', icon: <DollarSign size={22} /> },
    { id: 'settings', name: 'Restaurant Settings', icon: <Settings size={22} /> },
    { id: 'technical', name: 'Technical Issues', icon: <FileText size={22} /> }
  ];
  
  const popularArticles = [
    { id: 1, title: 'Setting up your restaurant profile', category: 'account', views: 1245 },
    { id: 2, title: 'Managing reservations and cancellations', category: 'reservations', views: 982 },
    { id: 3, title: 'How to update your menu items', category: 'settings', views: 756 },
    { id: 4, title: 'Understanding your billing cycle', category: 'payments', views: 634 },
    { id: 5, title: 'Troubleshooting login issues', category: 'technical', views: 521 }
  ];
  
  const faqs = [
    {
      id: 1,
      question: 'How do I update my restaurant\'s operating hours?',
      answer: 'You can update your restaurant\'s operating hours by logging into your Partner Dashboard, navigating to "Restaurant Settings" > "Hours & Availability" and making your changes. Updates typically appear on the platform within 1-2 hours.'
    },
    {
      id: 2,
      question: 'How can I respond to customer reviews?',
      answer: 'To respond to customer reviews, go to the "Reviews" section in your Partner Dashboard. Find the review you want to address and click on "Respond". Remember that professional and thoughtful responses show good customer service to potential diners.'
    },
    {
      id: 3,
      question: 'How do I update my menu on the platform?',
      answer: 'You can update your menu by going to "Menu Management" in your Partner Dashboard. You can add new items, edit existing ones, or update prices. You can also upload a new PDF menu if you prefer. Menu changes are usually reflected within 24 hours.'
    },
    {
      id: 4,
      question: 'What should I do if I need to temporarily close my restaurant?',
      answer: 'If you need to temporarily close your restaurant, you can set a temporary closure in "Restaurant Settings" > "Availability". This will prevent new reservations during the selected period while preserving your restaurant profile information.'
    },
    {
      id: 5,
      question: 'How are the service fees calculated?',
      answer: 'Service fees are calculated as a percentage of the reservation total, currently set at 2% per reservation. You can find detailed information about your fees in the "Billing & Payments" section of your Partner Dashboard.'
    }
  ];
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };
  
  const resetForm = () => {
    setContactForm({
      name: '',
      email: '',
      restaurantId: '',
      subject: '',
      category: '',
      message: '',
      priority: 'normal'
    });
    setSubmitted(false);
  };
  
  return (
    <div className="partner-support-page">
      {/* Hero Banner */}
      <div className="support-banner">
        <div className="banner-overlay"></div>
        <div className="container py-5 position-relative">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center text-white">
              <h1 className="display-4 fw-bold mb-3 hero-title">Partner Support Center</h1>
              <p className="lead mb-4 hero-subtitle">Get help with your restaurant account, reservations, and settings</p>
              
              <div className="search-container">
                <div className="input-group">
                  <span className="input-group-text border-0 bg-transparent">
                    <Search size={20} color="#495057" />
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-lg border-0 shadow-none"
                    placeholder="Search for help articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <button className="btn btn-primary px-4">Search</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Support Navigation */}
      <div className="support-navigation py-3 shadow-sm">
        <div className="container">
          <ul className="nav nav-pills justify-content-center">
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'help-center' ? 'active' : ''}`}
                onClick={() => setActiveTab('help-center')}
              >
                <HelpCircle size={16} className="me-2" />
                Help Center
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'contact-us' ? 'active' : ''}`}
                onClick={() => setActiveTab('contact-us')}
              >
                <MessageSquare size={16} className="me-2" />
                Contact Support
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${activeTab === 'faq' ? 'active' : ''}`}
                onClick={() => setActiveTab('faq')}
              >
                <FileText size={16} className="me-2" />
                FAQs
              </button>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="container py-5">
        {/* Help Center Tab */}
        {activeTab === 'help-center' && (
          <div className="help-center-tab">
            <div className="row">
              <div className="col-lg-8">
                <div className="categories-section mb-5">
                  <h2 className="fw-bold mb-4">Browse by Category</h2>
                  <div className="row g-4">
                    {categories.map(category => (
                      <div key={category.id} className="col-md-4 col-sm-6">
                        <div className="category-card">
                          <div className="category-icon">
                            {category.icon}
                          </div>
                          <h5 className="category-title">{category.name}</h5>
                          <div className="articles-count">24 articles</div>
                          <Link to={`#${category.id}`} className="stretched-link"></Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="popular-articles mb-5">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="fw-bold mb-0">Popular Articles</h2>
                    <Link to="#" className="view-all-link">
                      View All <ArrowRight size={14} />
                    </Link>
                  </div>
                  
                  <div className="articles-list">
                    {popularArticles.map(article => (
                      <div key={article.id} className="article-card">
                        <div className="article-meta">
                          <span className={`article-category ${article.category}`}>
                            {categories.find(c => c.id === article.category)?.name}
                          </span>
                          <span className="article-views">
                            <Users size={14} /> {article.views}
                          </span>
                        </div>
                        <h5 className="article-title">{article.title}</h5>
                        <Link to={`#article-${article.id}`} className="article-link">
                          Read article <ChevronRight size={14} />
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="getting-started">
                  <h2 className="fw-bold mb-4">Getting Started</h2>
                  <div className="getting-started-content">
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="quick-guide">
                          <h5>Quick Setup Guide</h5>
                          <p>Follow our step-by-step guide to set up your restaurant profile correctly and start accepting reservations in no time.</p>
                          <Link to="#" className="btn btn-outline-primary">
                            View Guide
                          </Link>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="video-guides">
                          <h5>Video Tutorials</h5>
                          <div className="video-thumbnails">
                            <div className="video-thumbnail">
                              <div className="thumbnail-overlay">
                                <span>3:45</span>
                              </div>
                              <img 
                                src="https://images.unsplash.com/photo-1516321497487-e288fb19713f?q=80&w=2070" 
                                alt="Dashboard Tutorial" 
                                className="img-fluid rounded"
                              />
                              <div className="video-title">Dashboard Tutorial</div>
                            </div>
                            <div className="video-thumbnail">
                              <div className="thumbnail-overlay">
                                <span>4:12</span>
                              </div>
                              <img 
                                src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740?q=80&w=2070" 
                                alt="Reservation Management" 
                                className="img-fluid rounded"
                              />
                              <div className="video-title">Reservation Management</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="support-sidebar">
                  <div className="contact-methods">
                    <h4 className="fw-bold mb-3">Contact Us</h4>
                    <div className="contact-method">
                      <div className="contact-icon">
                        <Phone size={20} />
                      </div>
                      <div className="contact-info">
                        <h6>Phone Support</h6>
                        <p className="mb-0">+1 (800) 123-4567</p>
                        <span className="text-muted small">Mon-Fri, 9am-6pm</span>
                      </div>
                    </div>
                    <div className="contact-method">
                      <div className="contact-icon">
                        <Mail size={20} />
                      </div>
                      <div className="contact-info">
                        <h6>Email Support</h6>
                        <p className="mb-0">partners@hallabeats.com</p>
                        <span className="text-muted small">24/7 support</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <button 
                        className="btn btn-primary w-100"
                        onClick={() => setActiveTab('contact-us')}
                      >
                        Submit Support Ticket
                      </button>
                    </div>
                  </div>
                  
                  <div className="emergency-support">
                    <h5>Need Emergency Help?</h5>
                    <p>For urgent issues requiring immediate attention during business hours.</p>
                    <a href="tel:+18001234567" className="btn btn-danger">
                      <Phone size={16} className="me-2" /> Call Emergency Support
                    </a>
                  </div>
                  
                  <div className="upcoming-webinars">
                    <h5>Upcoming Webinars</h5>
                    <div className="webinar-item">
                      <div className="webinar-date">
                        <div className="month">MAR</div>
                        <div className="day">15</div>
                      </div>
                      <div className="webinar-info">
                        <h6>Maximizing Reservations</h6>
                        <p className="small text-muted mb-0">
                          <Clock size={14} className="me-1" /> 2:00 PM EST
                        </p>
                      </div>
                    </div>
                    <div className="webinar-item">
                      <div className="webinar-date">
                        <div className="month">MAR</div>
                        <div className="day">22</div>
                      </div>
                      <div className="webinar-info">
                        <h6>Menu Optimization Tips</h6>
                        <p className="small text-muted mb-0">
                          <Clock size={14} className="me-1" /> 1:00 PM EST
                        </p>
                      </div>
                    </div>
                    <Link to="#" className="btn btn-outline-primary btn-sm w-100 mt-3">
                      View All Webinars
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Contact Support Tab */}
        {activeTab === 'contact-us' && (
          <div className="contact-support-tab">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="card border-0 shadow-lg">
                  <div className="card-body p-4 p-lg-5">
                    {!submitted ? (
                      <>
                        <h2 className="fw-bold mb-4 text-center">Submit a Support Request</h2>
                        <p className="text-center text-muted mb-4">
                          Fill out the form below and our support team will get back to you as soon as possible
                        </p>
                        
                        <form onSubmit={handleSubmit}>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <div className="form-floating">
                                <input
                                  type="text"
                                  className="form-control"
                                  id="name"
                                  name="name"
                                  placeholder="Your Name"
                                  value={contactForm.name}
                                  onChange={handleInputChange}
                                  required
                                />
                                <label htmlFor="name">Your Name</label>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-floating">
                                <input
                                  type="email"
                                  className="form-control"
                                  id="email"
                                  name="email"
                                  placeholder="Email Address"
                                  value={contactForm.email}
                                  onChange={handleInputChange}
                                  required
                                />
                                <label htmlFor="email">Email Address</label>
                              </div>
                            </div>
                          </div>
                          
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <div className="form-floating">
                                <input
                                  type="text"
                                  className="form-control"
                                  id="restaurantId"
                                  name="restaurantId"
                                  placeholder="Restaurant ID"
                                  value={contactForm.restaurantId}
                                  onChange={handleInputChange}
                                />
                                <label htmlFor="restaurantId">Restaurant ID (if applicable)</label>
                              </div>
                            </div>
                            <div className="col-md-6 mb-3">
                              <div className="form-floating">
                                <select
                                  className="form-select"
                                  id="category"
                                  name="category"
                                  value={contactForm.category}
                                  onChange={handleInputChange}
                                  required
                                >
                                  <option value="">Select category</option>
                                  {categories.map(category => (
                                    <option key={category.id} value={category.id}>
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                                <label htmlFor="category">Issue Category</label>
                              </div>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <div className="form-floating">
                              <input
                                type="text"
                                className="form-control"
                                id="subject"
                                name="subject"
                                placeholder="Subject"
                                value={contactForm.subject}
                                onChange={handleInputChange}
                                required
                              />
                              <label htmlFor="subject">Subject</label>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <div className="form-floating">
                              <textarea
                                className="form-control"
                                id="message"
                                name="message"
                                placeholder="Describe your issue"
                                style={{ height: '150px' }}
                                value={contactForm.message}
                                onChange={handleInputChange}
                                required
                              ></textarea>
                              <label htmlFor="message">Describe your issue</label>
                            </div>
                          </div>
                          
                          <div className="mb-4">
                            <label className="form-label">Priority Level</label>
                            <div className="d-flex flex-wrap gap-3">
                              <div className="form-check priority-option">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="priority"
                                  id="priorityLow"
                                  value="low"
                                  checked={contactForm.priority === 'low'}
                                  onChange={handleInputChange}
                                />
                                <label className="form-check-label" htmlFor="priorityLow">
                                  <div className="priority-indicator low"></div>
                                  <span>Low</span>
                                </label>
                              </div>
                              <div className="form-check priority-option">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="priority"
                                  id="priorityNormal"
                                  value="normal"
                                  checked={contactForm.priority === 'normal'}
                                  onChange={handleInputChange}
                                />
                                <label className="form-check-label" htmlFor="priorityNormal">
                                  <div className="priority-indicator normal"></div>
                                  <span>Normal</span>
                                </label>
                              </div>
                              <div className="form-check priority-option">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="priority"
                                  id="priorityHigh"
                                  value="high"
                                  checked={contactForm.priority === 'high'}
                                  onChange={handleInputChange}
                                />
                                <label className="form-check-label" htmlFor="priorityHigh">
                                  <div className="priority-indicator high"></div>
                                  <span>High</span>
                                </label>
                              </div>
                            </div>
                          </div>
                          
                          <div className="form-check mb-4">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="termsAgreement"
                              required
                            />
                            <label className="form-check-label" htmlFor="termsAgreement">
                              I understand that my restaurant information may be accessed by support staff to assist with this request
                            </label>
                          </div>
                          
                          <div className="text-center">
                            <button
                              type="submit"
                              className="btn btn-primary btn-lg px-5"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Submitting...
                                </>
                              ) : (
                                'Submit Support Request'
                              )}
                            </button>
                          </div>
                        </form>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <div className="success-icon mb-4">
                          <CheckCircle size={80} color="#4CAF50" />
                        </div>
                        <h2 className="fw-bold mb-3">Support Request Received</h2>
                        <p className="text-muted mb-4">
                          Thank you for reaching out. Your support ticket has been created and our team will respond within 24 hours. A confirmation email has been sent to your inbox.
                        </p>
                        <div className="ticket-info mb-4">
                          <div className="ticket-number">
                            <h6>Ticket Number</h6>
                            <p className="mb-0 fw-bold">HLBT-{Math.floor(100000 + Math.random() * 900000)}</p>
                          </div>
                          <div className="ticket-type">
                            <h6>Issue Category</h6>
                            <p className="mb-0 fw-bold">{categories.find(c => c.id === contactForm.category)?.name || 'General'}</p>
                          </div>
                          <div className="ticket-status">
                            <h6>Status</h6>
                            <p className="mb-0 fw-bold">Open</p>
                          </div>
                        </div>
                        <div className="d-flex flex-wrap justify-content-center gap-3">
                          <button
                            onClick={resetForm}
                            className="btn btn-outline-primary"
                          >
                            Submit Another Request
                          </button>
                          <button
                            onClick={() => setActiveTab('help-center')}
                            className="btn btn-primary"
                          >
                            Return to Help Center
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="support-info mt-5">
                  <div className="row g-4">
                    <div className="col-md-4">
                      <div className="support-info-card">
                        <div className="info-icon">
                          <Clock size={24} />
                        </div>
                        <h5>Response Time</h5>
                        <p>We aim to respond to all inquiries within 24 hours</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="support-info-card">
                        <div className="info-icon">
                          <HelpCircle size={24} />
                        </div>
                        <h5>Live Chat</h5>
                        <p>Available Monday to Friday, 9am to 6pm EST</p>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="support-info-card">
                        <div className="info-icon">
                          <Phone size={24} />
                        </div>
                        <h5>Phone Support</h5>
                        <p>Call us at +1 (800) 123-4567 for immediate assistance</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* FAQs Tab */}
        {activeTab === 'faq' && (
          <div className="faqs-tab">
            <div className="row">
              <div className="col-lg-8">
                <h2 className="fw-bold mb-4">Frequently Asked Questions</h2>
                
                <div className="faq-categories mb-4">
                  <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-outline-primary active">All</button>
                    {categories.map(category => (
                      <button key={category.id} className="btn btn-outline-primary">
                        {category.name}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="faq-section">
                  <div className="faq-search mb-4">
                    <div className="input-group">
                      <span className="input-group-text bg-white border-end-0">
                        <Search size={18} />
                      </span>
                      <input 
                        type="text" 
                        className="form-control border-start-0"
                        placeholder="Search in FAQs..." 
                      />
                    </div>
                  </div>
                  
                  <div className="accordion faq-accordion" id="faqAccordion">
                    {faqs.map((faq, index) => (
                      <div key={faq.id} className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#faq-${faq.id}`}
                            aria-expanded="false"
                            aria-controls={`faq-${faq.id}`}
                          >
                            {faq.question}
                          </button>
                        </h2>
                        <div
                          id={`faq-${faq.id}`}
                          className="accordion-collapse collapse"
                          data-bs-parent="#faqAccordion"
                        >
                          <div className="accordion-body">
                            <p>{faq.answer}</p>
                            <div className="faq-feedback">
                              <span>Was this helpful?</span>
                              <button className="btn btn-sm btn-success ms-2">Yes</button>
                              <button className="btn btn-sm btn-outline-secondary ms-2">No</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="still-need-help mt-5">
                  <div className="card bg-light border-0">
                    <div className="card-body p-4 text-center">
                      <h4 className="fw-bold mb-3">Still need help?</h4>
                      <p className="mb-4">If you couldn't find an answer to your question, our support team is here to help</p>
                      <button 
                        className="btn btn-primary px-5"
                        onClick={() => setActiveTab('contact-us')}
                      >
                        Contact Support
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="support-sidebar">
                  <div className="related-articles">
                    <h4 className="fw-bold mb-3">Related Articles</h4>
                    <ul className="article-list">
                      <li className="article-item">
                        <Link to="#" className="article-link">
                          <FileText size={16} className="me-2" />
                          How to update your menu items
                        </Link>
                      </li>
                      <li className="article-item">
                        <Link to="#" className="article-link">
                          <FileText size={16} className="me-2" />
                          Managing your reservation calendar
                        </Link>
                      </li>
                      <li className="article-item">
                        <Link to="#" className="article-link">
                          <FileText size={16} className="me-2" />
                          Setting up promotional discounts
                        </Link>
                      </li>
                      <li className="article-item">
                        <Link to="#" className="article-link">
                          <FileText size={16} className="me-2" />
                          Updating your restaurant photos
                        </Link>
                      </li>
                      <li className="article-item">
                        <Link to="#" className="article-link">
                          <FileText size={16} className="me-2" />
                          Understanding your analytics dashboard
                        </Link>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="downloadable-guides">
                    <h4 className="fw-bold mb-3">Downloadable Guides</h4>
                    <div className="guide-item">
                      <div className="guide-icon">
                        <FileText size={24} />
                      </div>
                      <div className="guide-info">
                        <h6>Complete Restaurant Partner Manual</h6>
                        <p className="mb-0 small">Complete guide to maximize your restaurant's success</p>
                        <Link to="#" className="guide-download">
                          Download PDF <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                    <div className="guide-item">
                      <div className="guide-icon">
                        <FileText size={24} />
                      </div>
                      <div className="guide-info">
                        <h6>Marketing Best Practices</h6>
                        <p className="mb-0 small">Tips to promote your restaurant effectively</p>
                        <Link to="#" className="guide-download">
                          Download PDF <ArrowRight size={14} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Contact Banner */}
      <div className="contact-banner py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7">
              <h2 className="text-white fw-bold mb-3">Need personalized assistance?</h2>
              <p className="text-white opacity-75 mb-0">Our partner success team is ready to help you optimize your restaurant's performance</p>
            </div>
            <div className="col-lg-5 text-lg-end mt-4 mt-lg-0">
              <a href="tel:+18001234567" className="btn btn-light btn-lg me-2">
                <Phone size={18} className="me-2" /> Call Us
              </a>
              <button 
                className="btn btn-outline-light btn-lg"
                onClick={() => setActiveTab('contact-us')}
              >
                <MessageSquare size={18} className="me-2" /> Send Message
              </button>
            </div>
          </div>
        </div>
      </div>
      

    </div>
  );
};

export default PartnerSupport;